'use client';

import { Box, Typography } from '@mui/material';
import Link from 'next/link';
import type { ReactNode } from 'react';
import type { SampleDataPackageRefDto, SampleDto } from '@/api/ApiClient';
import { FIELD_WIDTH } from '@/components/common/DetailField';
import { buildDataPackageHref, buildProjectSampleHref } from '@/lib/routing/appPaths';

const linkSx = {
  color: 'primary.main',
  cursor: 'pointer',
  '&:hover': { textDecoration: 'underline' },
} as const;

function commaLinkedLabels(items: { key: string; href: string; label: string }[]): ReactNode {
  return items.map((item, index) => (
    <span key={item.key}>
      <Link href={item.href} style={{ textDecoration: 'none', color: 'inherit' }}>
        <Typography component="span" variant="body1" sx={linkSx}>
          {item.label}
        </Typography>
      </Link>
      {index < items.length - 1 ? ', ' : null}
    </span>
  ));
}

/**
 * Projects the caller can actually open, plus snapshot data-package links.
 * Snapshots omit "No Project Assigned" when the project exists but is unpublished.
 */
export function SampleOwnershipFields({
  sample,
  projectLabels,
  basePath,
  fieldWidth = FIELD_WIDTH,
}: {
  sample: SampleDto;
  projectLabels: string[];
  basePath?: string;
  fieldWidth?: string | Record<string, string>;
}) {
  const packages: SampleDataPackageRefDto[] = sample.dataPackages ?? [];
  const sampleKey = sample.sampleLabel || String(sample.id ?? '');

  return (
    <>
      {projectLabels.length > 0 || !sample.isSnapshot ? (
        <Box sx={{ width: fieldWidth }}>
          <Typography variant="subtitle2" color="text.secondary">
            Projects
          </Typography>
          <Typography variant="body1">
            {projectLabels.length > 0
              ? commaLinkedLabels(
                  projectLabels.map((label) => ({
                    key: label,
                    href: buildProjectSampleHref(basePath, label, sampleKey),
                    label,
                  }))
                )
              : 'No Project Assigned'}
          </Typography>
        </Box>
      ) : null}
      {packages.length > 0 ? (
        <Box sx={{ width: fieldWidth }}>
          <Typography variant="subtitle2" color="text.secondary">
            Data packages
          </Typography>
          <Typography variant="body1">
            {commaLinkedLabels(
              packages.map((pkg) => ({
                key: String(pkg.id),
                href: buildDataPackageHref(basePath, pkg.routeKey?.trim() || String(pkg.id)),
                label: pkg.label?.trim() || `Data package ${pkg.id}`,
              }))
            )}
          </Typography>
        </Box>
      ) : null}
    </>
  );
}
